import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {ClassificationField} from "../../models/classification-field";
import {ClassificationType} from "../../models/classification-type";
import Helpers from "../../utils/helpers";

@Injectable()
export class ClassificationControllerService {

  private baseUrl: string = environment.BASE_URL;
  private gdbURL: string = environment.GDB_URL;

  constructor(private http: HttpClient) {
  }

  public getClassificationsList() {
    return this.http.get(this.baseUrl + "classifications");
  }

  public getClassificationsByService(serviceId: string) {
    return this.http.get(this.baseUrl + "classifications/byService/" + serviceId);
  }

  public getClassificationsGDB() {
    return this.http.get(this.baseUrl + "classifications?showGdbCatalogs=true");
  }

  public getClassificationsGDBvalue(dbVersion: string, dbCode: string, field: string) {
    // return this.http.get(this.gdbURL + `api/v1/data/${dbCode}/${dbVersion}/values/${field}`);
    return this.http.get(this.gdbURL + `api/v1/data/${dbCode}/any/values/${field}`);
  }

  public getClassificationsByType(classificationTypeId: string): Observable<ClassificationField[]> {
    return this.http.get(this.baseUrl + "classification/" + classificationTypeId) as Observable<ClassificationField[]>;
  }

  public getClassificationsByPage(classificationTypeId: string, page: number = 0, size: number = 30, filter?: string, sortKey: string = "customOrder", asc: string = "asc"): Observable<any> {
    const url = this.baseUrl + "classification/" + classificationTypeId + "/pageable?page=" + page + "&size=" + size + "&valueFilter=" + filter + "&sort=" + sortKey + "," + asc;
    return this.http.get(url) as Observable<any>;
  }

  public getClassificationTypesByType(classificationTypeId: string): Observable<ClassificationType[]> {
    return this.http.get(this.baseUrl + "classification_types/" + classificationTypeId) as Observable<ClassificationType[]>;
  }

  public getClassificationTypeById(classificationTypeId: string): Observable<ClassificationType[]> {
    return this.http.get(this.baseUrl + "classification_type/" + classificationTypeId) as Observable<ClassificationType[]>;
  }

  public exportClassificationCSV(classificationTypeId: string): Observable<string> {
    return this.http.get(this.baseUrl + "classification/" + classificationTypeId + "/exportCsv", { responseType: "text" }) as Observable<string>;
  }

  public getClassificationsWithGroupsByType(classificationTypeId: string): Observable<ClassificationField[]> {
    return this.http.get(this.baseUrl + "classification/" + classificationTypeId + "/classification_groups") as Observable<ClassificationField[]>;
  }

  public getFormClassifications() {
    return this.http.get(this.baseUrl + "form_classifications");
  }

  public getClassificationTypes(): Observable<any> {
    return this.http.get(this.baseUrl  + "classification") as Observable<any>;
  }

  public getClassificationTypesByPage(page: number = 0, size: number = 30, filter: string, sortKey: string = "name", asc: string = "asc", showRejections: boolean = false): Observable<any> {
    return this.http.get(this.baseUrl  + "classification?showRejections=" + showRejections + "&page=" + page + "&sort=" + sortKey + "," + asc + (size ? ("&size=" + size) : "") + (filter ? ("&nameFilter=" + filter) : "")) as Observable<any>;
  }

  public getServiceClassificationTypes(serviceId: string): Observable<any> {
    return this.http.get(this.baseUrl  + "service/" + serviceId + "/classification") as Observable<any>;
  }

  public getServiceClassificationTypesByPage(serviceId: string, page: number = 0, size: number = 30, filter: string, sortKey: string = "name", asc: string = "asc"): Observable<any> {
    return this.http.get(this.baseUrl  + "service/" + serviceId + "/classification?page=" + page + "&sort=" + sortKey + "," + asc + (size ? ("&size=" + size) : "") + (filter ? ("&nameFilter=" + filter) : "")) as Observable<any>;
  }

  public findClassificationTypesByName(name: string): Observable<ClassificationType[]> {
    return this.http.get(this.baseUrl + "search_classification/" + name) as Observable<ClassificationType[]>;
  }

  public deleteClassificationField(fieldId: string): Observable<string> {
    return this.http.delete(
      this.baseUrl + "classification/field/" + fieldId,
      { responseType: "text" }
    ) as Observable<string>;
  }

  public saveDocumentClassificationField(classificationTypeId: string, classificationField: ClassificationField): Observable<ClassificationField> {
    Helpers.trimRecursiveObj(classificationField);
    const body = JSON.stringify(classificationField);
    return this.http.post(this.baseUrl + "classification/" + classificationTypeId + "/field", body) as Observable<ClassificationField>;
  }

  public createClassificationType(classificationType: ClassificationType) {
    Helpers.trimRecursiveObj(classificationType);
    const body = JSON.stringify(classificationType);
    return this.http.post(this.baseUrl + "classification", body);
  }

  public updateClassificationType(classificationType: ClassificationType) {
    Helpers.trimRecursiveObj(classificationType);
    const body = JSON.stringify(classificationType);
    return this.http.put(this.baseUrl + "classification", body, {responseType: "text"});
  }

  public copyClassification(classificationTypeId: string) {
    return this.http.post(this.baseUrl + "classification/copy/" + classificationTypeId, {});
  }

  public generateCustomKeys(classificationTypeId: string): Observable<ClassificationField[]> {
    return this.http.post(this.baseUrl + "classification/generateKeys/" + classificationTypeId, {}) as Observable<ClassificationField[]>;
  }

  public deleteClassificationType(classificationTypeId: string) {
    return this.http.delete(this.baseUrl + "classification/" + classificationTypeId + "?deleteRelatedDeterminants=true", {responseType: "text"});
  }

  public getClassificationFieldByGroupId(groupId: string): Observable<ClassificationField[]> {
    return this.http.get(this.baseUrl + "classification_field_by_groups/" + groupId) as Observable<ClassificationField[]>;
  }

  public deleteClassificationTypes(classificationTypes: ClassificationType[]) {
    const body = JSON.stringify(classificationTypes);
    return this.http.post(this.baseUrl + "classifications", body, {responseType: "text"});
  }

  public addSubCatalogs(classificationTypeId: string, classificationFields: ClassificationField[]): Observable<ClassificationField[]> {
    return this.http.post(this.baseUrl + `classification/${classificationTypeId}/addSubcatalogs`, classificationFields) as Observable<ClassificationField[]>;
  }

  public getClassificationUsages(classificationTypeIds: string[]): Observable<any[]> {
    return this.http.post(this.baseUrl + "classification/usages", classificationTypeIds) as Observable<any[]>;
  }
}
